import { Article } from '@/types/Article';

type FrontmatterValue = string | string[];

const articleSources = import.meta.glob('../../content/articles/**/*.article.html', {
    eager: true,
    query: '?raw',
    import: 'default',
}) as Record<string, string>;

const parseFrontmatter = (
    source: string,
): { data: Record<string, FrontmatterValue>; content: string } => {
    const match = source.match(/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/);
    if (!match) return { data: {}, content: source.trim() };

    const data: Record<string, FrontmatterValue> = {};
    const parseValue = (rawValue: string): string => {
        if (
            (rawValue.startsWith('"') && rawValue.endsWith('"')) ||
            (rawValue.startsWith("'") && rawValue.endsWith("'"))
        ) {
            try {
                return JSON.parse(rawValue);
            } catch {
                return rawValue.replace(/^["']|["']$/g, '');
            }
        }
        return rawValue;
    };
    match[1].split('\n').forEach((line) => {
        const separatorIndex = line.indexOf(':');
        if (separatorIndex === -1) return;

        const key = line.slice(0, separatorIndex).trim();
        const rawValue = line.slice(separatorIndex + 1).trim();
        if (rawValue.startsWith('[') && rawValue.endsWith(']')) {
            data[key] = rawValue
                .slice(1, -1)
                .split(',')
                .map((value) => value.trim().replace(/^["']|["']$/g, ''))
                .filter(Boolean);
            return;
        }
        data[key] = parseValue(rawValue);
    });

    return { data, content: match[2].trim() };
};

const stringValue = (
    data: Record<string, FrontmatterValue>,
    key: string,
    fallback = '',
): string => {
    const value = data[key];
    return typeof value === 'string' && value ? value : fallback;
};

const articleId = (locale: string, slug: string): string => (locale === 'en' ? `en_${slug}` : slug);

const pathInfo = (sourcePath: string): { locale: string; slug: string } | null => {
    const match = sourcePath.match(/\/content\/articles\/([a-z]{2})\/([^/]+)\.article\.html$/);
    if (!match) return null;
    return { locale: match[1], slug: match[2] };
};

const localArticles = Object.entries(articleSources)
    .map(([sourcePath, source]) => {
        const info = pathInfo(sourcePath);
        if (!info) return null;
        const { data, content } = parseFrontmatter(source);
        if (data.status === '非公開') return null;

        const publishedAt = stringValue(
            data,
            'publishedAt',
            stringValue(data, 'createdAt', new Date(0).toISOString()),
        );
        const updateAt = stringValue(data, 'updatedAt', publishedAt);
        const title = stringValue(data, 'title', info.slug);
        const description = stringValue(
            data,
            'description',
            stringValue(data, 'searchIntent', title),
        );
        const imagesJson = stringValue(data, 'imagesJson');
        const ogImage = stringValue(data, 'ogImage');

        return {
            id: articleId(info.locale, info.slug),
            publishedAt,
            updateAt,
            title,
            description,
            ogImage: ogImage || undefined,
            content,
            images: imagesJson || undefined,
        } satisfies Article;
    })
    .filter((article): article is Article => Boolean(article));

const articleById = new Map(localArticles.map((article) => [article.id, article]));

export const getLocalArticle = (id: string): Article | null => articleById.get(id) ?? null;

export const hasLocalArticle = (id: string): boolean => articleById.has(id);
